import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Download, Settings, Bluetooth } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import axios from 'axios';
import PrintableInvoice from './PrintableInvoice';
import { escposPrinter } from '../utils/escpos';

export default function PrintDialog({ rental, open, onClose }) {
  const [paperSize, setPaperSize] = useState('58mm'); // Default 58mm
  const [showSettings, setShowSettings] = useState(false);
  const printRef = useRef(null);
  const [savedPrinter, setSavedPrinter] = useState(escposPrinter.getSavedPrinterName());
  const [businessProfile, setBusinessProfile] = useState(null);

  // Fetch business profile only
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const profileResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/business-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (profileResponse.data.success && profileResponse.data.profile) {
          setBusinessProfile(profileResponse.data.profile);
        }
      } catch (err) {
        console.error('Error fetching business profile:', err);
      }
    };
    
    if (open) {
      fetchBusinessProfile();
    }
  }, [open]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const [printing, setPrinting] = useState(false);

  const onPrintClick = () => {
    try {
      setTimeout(() => {
        handlePrint();
        toast.success('Nota berhasil dicetak!');
      }, 100);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Gagal mencetak nota');
    }
  };

  // ESC/POS Bluetooth Print for Rental
  const onBluetoothPrint = async (forceNew = false) => {
    if (!rental) return;
    
    setPrinting(true);
    try {
      toast.loading('Connecting to printer...', { id: 'bt-print' });
      
      await escposPrinter.connect(forceNew);
      
      // CRITICAL: Add extra stabilization delay after connection
      // This ensures device is fully ready before sending print commands
      console.log(' Waiting for printer stabilization...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.loading('Printing...', { id: 'bt-print' });
      
      // Print rental receipt directly (not F&B format)
      await escposPrinter.printRentalReceipt(rental);
      
      // Update saved printer name
      setSavedPrinter(escposPrinter.getSavedPrinterName());
      
      toast.success(' Struk berhasil dicetak!', { id: 'bt-print' });
      
      // Keep connection alive (don't disconnect)
      
    } catch (error) {
      console.error('Bluetooth print error:', error);
      toast.error(' Gagal print: ' + error.message, { id: 'bt-print' });
    } finally {
      setPrinting(false);
    }
  };

  // Change printer
  const onChangePrinter = () => {
    escposPrinter.forgetPrinter();
    setSavedPrinter('No printer saved');
    toast.info('Printer dilupakan. Print berikutnya akan minta pilih printer baru.');
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Membuat PDF...', { id: 'pdf-download' });
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (printRef.current) {
        const canvas = await html2canvas(printRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: paperSize === '58mm' ? [58, 297] : paperSize === '80mm' ? [80, 297] : 'a4'
        });
        
        const imgWidth = paperSize === '58mm' ? 58 : paperSize === '80mm' ? 80 : 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`Nota-${rental.customer_name}-${new Date().toISOString()}.pdf`);
        
        toast.success('PDF berhasil diunduh!', { id: 'pdf-download' });
      }
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Gagal membuat PDF', { id: 'pdf-download' });
    }
  };

  if (!open || !rental) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Cetak Struk</h3>
            <p className="text-blue-100 text-sm mt-1">Pembayaran berhasil!</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Preview */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm font-semibold text-gray-700">Preview Struk</p>
              <p className="text-xs text-gray-500 mt-1">{rental.customer_name}</p>
            </div>
            
            {/* Detailed preview - Match print format */}
            <div className="bg-white p-4 rounded shadow-sm text-xs border space-y-2">
              <div className="text-center border-b pb-2">
                {/* Business Name - smaller font */}
                <p className="font-bold text-xs leading-tight">{businessProfile?.business_name?.toUpperCase() || 'RIMURU GAME SUITE'}</p>
                {/* Branch Name - USE RENTAL.BRANCH (UPDATED IN DB) */}
                <p className="text-gray-700 text-xs font-bold mt-0.5">
                  {rental.branch || 'Loading...'}
                </p>
                {/* Powered by */}
                <p className="text-gray-500 text-xs mt-1">Powered by Rimuru Game Suite</p>
              </div>
              
              {/* Kasir Info - Match print format */}
              <div className="border-b pb-2">
                <div className="flex justify-between text-gray-600">
                  <span>Kasir:</span>
                  <span>{rental.operator_name || JSON.parse(localStorage.getItem('user') || '{}').full_name || 'Unknown'}</span>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-semibold select-none">{rental.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Unit:</span>
                  <span className="font-semibold select-none">{rental.ps_unit_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mode:</span>
                  <span className="font-semibold uppercase select-none">{rental.rental_mode || 'Durasi'}</span>
                </div>
                {rental.package_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paket:</span>
                    <span className="font-semibold">{rental.package_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Durasi:</span>
                  <span className="font-semibold">{rental.actual_duration_hours ? `${rental.actual_duration_hours.toFixed(1)} jam` : `${rental.duration_hours || 0} jam`}</span>
                </div>
              </div>
              
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Harga Rental:</span>
                  <span>Rp {((rental.package_price || rental.base_rental_price || (rental.final_price || rental.total_price || 0) - (rental.additional_items || []).reduce((sum, item) => sum + (item.price * item.qty), 0))).toLocaleString('id-ID')}</span>
                </div>
                
                {rental.additional_items && rental.additional_items.length > 0 && (
                  <>
                    <div className="text-gray-600 font-semibold mt-2">F&B Items:</div>
                    {rental.additional_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between pl-2">
                        <span className="text-gray-600">{item.qty}x {item.name}</span>
                        <span>Rp {(item.price * item.qty).toLocaleString('id-ID')}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
              
              <div className="border-t-2 border-dashed pt-2">
                <div className="flex justify-between font-bold text-sm">
                  <span>TOTAL:</span>
                  <span>Rp {(rental.final_price || rental.total_price || 0).toLocaleString('id-ID')}</span>
                </div>
                <div className="flex justify-between text-gray-600 mt-1">
                  <span>Metode:</span>
                  <span className="uppercase">{rental.payment_method || '-'}</span>
                </div>
              </div>
              
              <div className="text-center text-gray-500 text-xs pt-2 border-t">
                {new Date(rental.paid_at || rental.actual_end_time).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })}
              </div>
            </div>
          </div>

          {/* Paper Size Settings */}
          <div className="mb-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Pengaturan Kertas</span>
              </div>
              <span className="text-xs text-gray-500">{paperSize}</span>
            </button>
            
            {showSettings && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
                <label className="text-xs text-gray-600 font-medium mb-2 block">
                  Ukuran Kertas Thermal:
                </label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value)}
                  className="w-full bg-white px-3 py-2 rounded-lg text-sm border border-gray-300 focus:border-blue-500 focus:outline-none"
                >
                  <option value="58mm">58mm (Kecil)</option>
                  <option value="80mm">80mm (Standar)</option>
                  <option value="custom">Custom (Besar)</option>
                </select>
              </div>
            )}
          </div>

          {/* Saved Printer Info */}
          {savedPrinter !== 'No printer saved' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bluetooth className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-600">Printer tersimpan:</p>
                    <p className="text-sm font-semibold text-gray-800">{savedPrinter}</p>
                  </div>
                </div>
                <button
                  onClick={onChangePrinter}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Ganti
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* ESC/POS Bluetooth Print (RECOMMENDED) */}
            <button
              onClick={() => onBluetoothPrint(false)}
              disabled={printing}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-4 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
            >
              <Bluetooth className="h-5 w-5" />
              {printing ? 'Printing...' : savedPrinter === 'No printer saved' ? 'Setup Printer & Print' : 'Print via Bluetooth'}
            </button>
            
            {/* Browser Print & PDF (Fallback) */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onPrintClick}
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors shadow-md text-sm"
              >
                <Printer className="h-4 w-4" />
                Browser
              </button>
              
              <button
                onClick={handleDownloadPDF}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors shadow-md text-sm"
              >
                <Download className="h-4 w-4" />
                PDF
              </button>
            </div>
            
            <button
              onClick={onClose}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Tutup
            </button>
          </div>
        </div>

        {/* Hidden PrintableInvoice */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <PrintableInvoice
            ref={printRef}
            rental={rental}
            paperSize={paperSize}
          />
        </div>
      </div>
    </div>
  );

  // Render modal using Portal to attach it directly to document.body
  return createPortal(modalContent, document.body);
}
